import { Link } from "react-router-dom";
import { Mail, Phone, MessageCircle, Cookie, Instagram, Facebook, Youtube } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import logoAudrivio from "@/assets/logo-audrivio.png";
import { trackContact } from "@/lib/trackContact";

const WHATSAPP_NUMBER = "393511294525";
const WHATSAPP_HREF = `https://wa.me/${WHATSAPP_NUMBER}`;
const WHATSAPP_DISPLAY = "+39 351 129 4525";

export default function Footer() {
  const { t } = useLang();
  return (
    <footer className="bg-xd-dark text-slate-300 border-t border-xd-darkBorder mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
        <div className="col-span-2">
          <Link to="/" className="inline-flex items-center gap-2 text-white">
            <img
              src={logoAudrivio}
              alt="audrivio"
              className="h-24 sm:h-28 w-auto object-contain drop-shadow-[0_0_14px_rgba(23,199,214,0.4)]"
            />
          </Link>
          <p className="mt-4 text-sm text-slate-400 max-w-sm leading-relaxed">
            {t("footer_tagline")}
          </p>
          <div className="flex gap-3 mt-5">
            <a href="#" aria-label="Instagram" className="w-9 h-9 rounded-lg border border-xd-darkBorder hover:border-xd-cyan flex items-center justify-center transition-colors"><Instagram className="w-4 h-4" /></a>
            <a href="#" aria-label="Facebook" className="w-9 h-9 rounded-lg border border-xd-darkBorder hover:border-xd-cyan flex items-center justify-center transition-colors"><Facebook className="w-4 h-4" /></a>
            <a href="#" aria-label="Youtube" className="w-9 h-9 rounded-lg border border-xd-darkBorder hover:border-xd-cyan flex items-center justify-center transition-colors"><Youtube className="w-4 h-4" /></a>
          </div>
        </div>

        <div>
          <div className="text-white font-semibold mb-3 font-display">{t("footer_shop")}</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/catalogo" className="hover:text-white">{t("nav_catalog")}</Link></li>
            <li><Link to="/marca/fiat" className="hover:text-white">Fiat</Link></li>
            <li><Link to="/marca/volkswagen" className="hover:text-white">Volkswagen</Link></li>
            <li><Link to="/marca/audi" className="hover:text-white">Audi</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-white font-semibold mb-3 font-display">{t("footer_support")}</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/contatti" className="hover:text-white">{t("nav_contact")}</Link></li>
            <li><Link to="/traccia-ordine" data-testid="footer-link-track" className="hover:text-white">{t("footer_track_order")}</Link></li>
            <li><Link to="/blog" className="hover:text-white">{t("footer_guides")}</Link></li>
            <li><a href="mailto:support@audrivio.com" data-testid="footer-email-link" onClick={() => trackContact("email", "footer")} className="hover:text-white inline-flex items-center gap-2"><Mail className="w-4 h-4" /> support@audrivio.com</a></li>
            <li>
              <a href={WHATSAPP_HREF} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-link" onClick={() => trackContact("whatsapp", "footer")} className="hover:text-white inline-flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> {WHATSAPP_DISPLAY}
              </a>
            </li>
            <li className="flex items-center gap-2 text-slate-500"><Phone className="w-4 h-4" /> {t("footer_support_hours")}</li>
          </ul>
        </div>

        <div>
          <div className="text-white font-semibold mb-3 font-display">{t("footer_legal")}</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/termini" data-testid="footer-link-terms" className="hover:text-white">{t("policy_terms")}</Link></li>
            <li><Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-white">{t("policy_privacy")}</Link></li>
            <li><Link to="/cookie" data-testid="footer-link-cookies" className="hover:text-white">{t("policy_cookies")}</Link></li>
            <li><Link to="/rimborsi" data-testid="footer-link-refund" className="hover:text-white">{t("policy_refund")}</Link></li>
            <li><Link to="/spedizioni" data-testid="footer-link-shipping" className="hover:text-white">{t("policy_shipping")}</Link></li>
            <li><Link to="/note-legali" data-testid="footer-link-legal" className="hover:text-white">{t("policy_legal")}</Link></li>
            <li>
              <button
                type="button"
                data-testid="footer-manage-cookies"
                onClick={() => window.dispatchEvent(new Event("audrivio:open-cookie-banner"))}
                className="text-left hover:text-white inline-flex items-center gap-2 text-xd-cyan/80 hover:text-xd-cyan"
              >
                <Cookie className="w-3.5 h-3.5" /> {t("footer_manage_cookies")}
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-xd-darkBorder">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500">
          <div className="max-w-md">
            <div className="font-display text-sm text-slate-300 mb-1">{t("footer_newsletter_title")}</div>
            <form className="flex gap-2 min-w-[280px]" onSubmit={(e) => e.preventDefault()}>
              <input type="email" placeholder={t("newsletter_placeholder")} data-testid="footer-newsletter-input" className="flex-1 min-w-0 bg-xd-dark2 border border-xd-darkBorder rounded-md px-3 py-2 text-sm focus:outline-none focus:border-xd-cyan text-slate-200" />
              <button type="submit" data-testid="footer-newsletter-btn" className="btn-cta px-4 py-2 rounded-md text-sm">{t("subscribe")}</button>
            </form>
          </div>
          <div className="text-center md:text-right">
            <div>© {new Date().getFullYear()} audrivio. {t("footer_rights")}</div>
            <div className="mt-1 font-mono-tech text-[10px] uppercase tracking-widest text-slate-600">Plug&Play head units</div>
          </div>
        </div>
      </div>
    </footer>
  );
}
